<template>
  <div>
    <div v-if="isLoading" class="d-flex justify-content-center mb-3 pt-4">
      <b-spinner style="width: 5rem; height: 5rem" variant="warning" label="Spinning"></b-spinner>
    </div>
    <div v-else>
      <div class="ml-3 mr-3" rounded="lg" id="overlay">
        <div class="row no-gutters mb-2 mt-4">
          <div class="col">
            <h1 class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-credit-card pr-1" data-test-hook="titleIcon" />
              <span data-test-hook="payment-information-title">{{ $t('xPaymentInformation') }}</span>
            </h1>
          </div>
        </div>
      </div>
      <div class="card" header-bg-variant="white">
        <div class="card-body">
          <div class="row mb-2 ml-0">
            <i class="far fa-credit-card colorOrange"></i>
            <strong class="ml-2" data-test-hook="account-holder-payment-information-account-settings">
              {{ $t('xPaymentInformation') }}
            </strong>
            <popper trigger="hover" :options="{ placement: 'right' }" class="ml-4">
              <div class="popper">
                {{ $t('xPrimaryPaymentInformationMessage') }}
              </div>
              <i slot="reference" class="far fa-question-circle "></i>
            </popper>
          </div>
          <div v-if="activeSubscriptionPlatform === appleStore">
            <div class="row">
              <div class="col-auto">
                <p data-test-hook="account-holder-payment-information-apple-subscription-header">
                  {{ $t('xPlanManagedByApple') }}
                </p>
                <div v-if="subscriptionIsDunning" class="card mt-3 bg-statusWarning">
                  <div class="card-body">
                    <div class="row mb-2">
                      <div class="col">
                        <span class="mb-0 pb-0" data-test-hook="apple-payment-account-needs-attention">
                          <i
                            class="far fa-triangle-exclamation pr-1"
                            data-test-hook="apple-payment-account-needs-attention-icon"
                          />
                          <span
                            style="font-size:1rem; font-weight: bold;"
                            data-test-hook="apple-payment-account-needs-attention-text"
                            >{{ $t('xYourAccountNeedsAttentionHeader') }}</span
                          >
                        </span>
                      </div>
                    </div>
                    <div class="row mb-2 ml-2">
                      <div class="col">
                        <span data-test-hook="apple-payment-method-issue-header-text">
                          {{ $t('xPaymentMethodIssue') }}
                        </span>
                      </div>
                    </div>
                    <div class="row mb-1 ml-2">
                      <div class="col">
                        <span data-test-hook="apple-payment-method-issue-body-text">
                          {{ $t('xApplePaymentMethodIssueBodyText') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="mt-4" data-test-hook="account-holder-payment-information-apple-subscription-body">
                  {{ $t('xUpdatePaymentMethodApple') }}
                </p>
              </div>
            </div>
          </div>
          <div v-else-if="activeSubscriptionPlatform === googleStore">
            <div class="row">
              <div class="col-auto">
                <p data-test-hook="account-holder-payment-information-google-subscription-header">
                  {{ $t('xPlanManagedByGoogle') }}
                </p>
                <div v-if="subscriptionIsDunning" class="card mt-3 bg-statusWarning">
                  <div class="card-body">
                    <div class="row mb-2">
                      <div class="col">
                        <span class="mb-0 pb-0" data-test-hook="google-payment-account-needs-attention">
                          <i
                            class="far fa-triangle-exclamation pr-1"
                            data-test-hook="google-payment-account-needs-attention-icon"
                          />
                          <span
                            style="font-size:1rem; font-weight: bold;"
                            data-test-hook="google-payment-account-needs-attention-text"
                            >{{ $t('xYourAccountNeedsAttentionHeader') }}</span
                          >
                        </span>
                      </div>
                    </div>
                    <div class="row mb-2 ml-2">
                      <div class="col">
                        <span data-test-hook="google-payment-method-issue-header-text">
                          {{ $t('xPaymentMethodIssue') }}
                        </span>
                      </div>
                    </div>
                    <div class="row mb-1 ml-2">
                      <div class="col">
                        <span data-test-hook="google-payment-method-issue-body-text">
                          {{ $t('xGooglePaymentMethodIssueBodyText') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="mt-4" data-test-hook="account-holder-payment-information-google-subscription-body">
                  {{ $t('xUpdatePaymentMethodGoogle') }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="paymentInfo">
            <div v-if="isMobileSubscription">
              <hr class="mb-4 mt-4" />
              <div class="row mb-2 ml-0">
                <strong data-test-hook="account-holder-unused-payment-information">
                  {{ $t('xUnusedPaymentInformation') }}
                </strong>
              </div>
              <div class="row">
                <div class="col-auto">
                  <p data-test-hook="account-holder-unused-payment-information-body">
                    {{ $t('xUnusedPaymentInformationBodyText') }}
                  </p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-auto">
                <p data-test-hook="account-holder-payment-information-card-account-settings">
                  {{ paymentInfo.cardType.toUpperCase() + ' - ' + paymentInfo.cardSuffix }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-auto">
                <p data-test-hook="account-holder-payment-information-expiration-account-settings">
                  {{
                    $t('xExpires') + ' ' + displayMonth(paymentInfo.expirationMonth) + '/' + paymentInfo.expirationYear
                  }}
                </p>
              </div>
            </div>
            <div v-if="!isMobileSubscription && subscriptionIsDunning" class="card mt-3 bg-statusWarning">
              <div class="card-body">
                <div class="row mb-2">
                  <div class="col">
                    <span class="mb-0 pb-0" data-test-hook="web-payment-account-needs-attention">
                      <i
                        class="far fa-triangle-exclamation pr-1"
                        data-test-hook="web-payment-account-needs-attention-icon"
                      />
                      <span
                        style="font-size:1rem; font-weight: bold;"
                        data-test-hook="web-payment-account-needs-attention-text"
                        >{{ $t('xYourAccountNeedsAttentionHeader') }}</span
                      >
                    </span>
                  </div>
                </div>
                <div class="row mb-2 ml-2">
                  <div class="col">
                    <span data-test-hook="web-payment-method-issue-header-text">
                      {{ $t('xPaymentMethodIssuePleaseUpdate') }}
                    </span>
                  </div>
                </div>
                <div class="row ml-2">
                  <div class="col">
                    <button
                      type="button"
                      class="btn btn-warning btn-update"
                      @click="openModal('update-payment-method')"
                      data-test-hook="web-payment-method-issue-button"
                      :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                    >
                      {{ $t('xUpdatePaymentInformation') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="shouldShowUpdatePayment" class="row">
              <div class="col">
                <button
                  type="button"
                  class="btn btn-outline-warning mt-3"
                  @click="openModal('update-payment-method')"
                  :title="$t('xUpdatePaymentInformation')"
                  data-test-hook="update-payment-information-button-account-settings"
                  :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                >
                  {{ $t('xUpdatePaymentInformation') }}
                </button>
              </div>
            </div>
            <div v-else-if="shouldShowRemovePayment" class="row">
              <div class="col">
                <button
                  type="button"
                  class="btn btn-outline-danger rounded-pill mt-3"
                  @click="removePaymentInformation()"
                  :title="$t('xRemovePaymentInformation')"
                  data-test-hook="remove-payment-information-button-account-settings"
                  :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                >
                  <i class="fas fa-trash-alt colorOrange"></i>
                  {{ $t('xRemovePaymentInformation') }}
                  <span class="spinner-border ml-2" v-if="removing"></span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="!paymentInfo && !isMobileSubscription">
            <div>
              {{ $t('xNoSavedPaymentInformation') }}
            </div>
          </div>
        </div>
      </div>
      <Modal modalId="update-payment-method" size="modal-lg" @hidden="closePaymentModal()">
        <ModalHeader :title="$t('xUpdatePaymentMethod')" icon="fal fa-credit-card colorOrange mr-1">
          <div class="cols-auto">
            <button
              type="button"
              class="btn btn-icon-button btn-icon-button--gray"
              @click="closePaymentModal(), (useExistingBillingInformation = true)"
              data-test-hook="CloseModalBtn"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </ModalHeader>
        <ModalBody>
          <div role="alert" v-if="showValidationBanner" class="alert alert-danger" data-test-hook="ValidationAlert">
            <p>{{ $t('xExperiencedTheseErrors') }}</p>
            <ul v-for="(message, index) in errorMessages" :key="index">
              <li :id="index" data-test-hook="ValidationMsg">
                {{ message }}
              </li>
            </ul>
          </div>
          <div role="alert" v-if="addressSearchError" class="alert alert-danger">
            <h4 class="alert-heading">{{ $t('xSomethingWentWrong') }}</h4>
            <p class="alert-body">{{ $t('xWaitAndTryAgainErrorText') }}</p>
          </div>
          <div v-if="!isShowingValidationView">
            <div class="row">
              <div class="col-12">
                <h3>
                  <strong data-test-hook="CcInfoHeader">{{ $t('xCreditCardInfo') }}</strong>
                  <span class="body-text-color">*</span>
                </h3>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <i class="asterisk-text" data-test-hook="UpdatePaymentWarningMsg">
                  {{ $t('xUpdatingPaymentWarning') }}
                </i>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group" id="credit-card-number">
                  <input
                    class="form-control"
                    id="creditCardNumber"
                    v-model="$v.creditCardNumber.$model"
                    maxlength="16"
                    type="text"
                    :class="status($v.creditCardNumber)"
                    v-mask="'################'"
                    :placeholder="$t('xCreditCardNumber')"
                    aria-describedby="card-number-invalid"
                    data-test-hook="CcNumber"
                  />
                  <div class="invalid-feedback" id="card-number-invalid" data-test-hook="invalidCcNo">
                    {{ $t('xCCFieldValidation') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-4">
                <div class="form-group" id="expiry-date-month">
                  <input
                    class="form-control"
                    id="xNm"
                    v-model="$v.expirationMonth.$model"
                    maxlength="2"
                    type="text"
                    :class="status($v.expirationMonth)"
                    v-mask="'##'"
                    :placeholder="$t('xMm')"
                    aria-describedby="expiry-month-invalid"
                    data-test-hook="CcExpireMonth"
                  />
                  <div class="invalid-feedback" id="expiry-month-invalid" data-test-hook="invalidCcMth">
                    {{ $t('xExpirationMonthValidation') }}
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="form-group" id="expiry-date-year">
                  <input
                    class="form-control"
                    v-model="$v.expirationYear.$model"
                    type="text"
                    :placeholder="$t('xYy')"
                    maxlength="2"
                    v-mask="'##'"
                    :class="status($v.expirationYear)"
                    aria-describedby="expiry-year-invalid"
                    data-test-hook="CcExpireYear"
                  />
                  <div class="invalid-feedback" id="expiry-year-invalid" data-test-hook="invalidCcYear">
                    {{ $t('xExpirationYearValidation') }}
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="input-group" id="cvv">
                  <input
                    class="form-control"
                    v-model="$v.cvv.$model"
                    type="text"
                    v-mask="'####'"
                    maxlength="4"
                    :placeholder="$t('xCvv')"
                    :class="status($v.cvv)"
                    aria-describedby="cvv-invalid"
                    data-test-hook="CcCvv"
                  />
                  <div class="input-group-append">
                    <div class="input-group-text">
                      <img
                        :height="20"
                        :src="require('@/assets/images/3-digit-code.svg')"
                        data-test-hook="ccCvvImage"
                      />
                    </div>
                  </div>
                  <div class="invalid-feedback" id="cvv-invalid" data-test-hook="invalidCcCvv">
                    {{ $t('xCVVValidation') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="accountData">
            <div v-if="!isShowingValidationView">
              <div class="row">
                <div class="col-12">
                  <h3>
                    <strong data-test-hook="BillingInformationTitle">{{ $t('xBillingInformationLowercase') }}</strong>
                  </h3>
                </div>
              </div>
              <div class="row mb-2">
                <div class="col">
                  <div class="custom-control custom-checkbox">
                    <input
                      id="useExistingBillingInformation"
                      type="checkbox"
                      class="custom-control-input"
                      v-model="useExistingBillingInformation"
                      :disabled="savingAccountHolder"
                      data-test-hook="UseCurrentBillingAddressChkBox"
                    />
                    <label
                      class="custom-control-label"
                      for="useExistingBillingInformation"
                      data-test-hook="useCurrBillingInfoText"
                    >
                      {{ $t('xUseCurrentBillingInfo') }}
                    </label>
                  </div>
                </div>
              </div>
              <div class="row" v-if="useExistingBillingInformation">
                <div class="col-12" data-test-hook="ExistingBillingName">
                  {{ accountProfile.firstName }} {{ accountProfile.lastName }}
                </div>
                <div class="col-12" data-test-hook="ExistingBillingAddress">
                  {{ DisplayAddress(accountProfile.address) }}
                </div>
              </div>
            </div>
            <div v-if="!useExistingBillingInformation">
              <div v-if="!isShowingValidationView">
                <div class="row">
                  <div class="col-12">
                    <fieldset id="firstName-group" class="form-group">
                      <div>
                        <input
                          class="form-control"
                          type="text"
                          id="firstName"
                          v-model="accountData.firstName"
                          data-test-hook="firstName"
                        />
                      </div>
                    </fieldset>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <fieldset class="form-group" id="lastName-group">
                      <div>
                        <input
                          class="form-control"
                          id="lastName"
                          v-model="accountData.lastName"
                          type="text"
                          data-test-hook="lastName"
                        />
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col">
                  <AddressSearchModals
                    ref="updateCardAddressSearchModalsRef"
                    addressSearchModalId="update-card-address-search-form"
                    addressValidationModalId="update-card-address-validation-form"
                    @onSubmit="saveAccountHolder"
                    @addressSearchModalViewChanged="handleValidationStateChange"
                    @setIsNextDisabled="handleSetIsNextDisabled"
                    @addressSearchError="handleAddressSearchError"
                    :inline="true"
                    :showContinueButton="false"
                    :showValidationButtons="false"
                    :showAddressCheckmarkHeader="false"
                    :isBillingAddress="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            v-if="useExistingBillingInformation"
            class="btn btn-block btn-primary"
            @click="payForProduct()"
            :disabled="!canPay()"
            data-test-hook="SaveChangesBtn"
          >
            {{ $t('xSave') }}
            <span class="spinner-border ml-2" v-if="paying"></span>
          </button>
          <button
            v-if="!useExistingBillingInformation && !isShowingValidationView"
            variant="outline-warning"
            class="btn btn-outline-warning btn-block"
            @click="addBillingAddress()"
            data-test-hook="SaveChangesBtn"
          >
            {{ $t('xConfirmBillingInfo') }}
            <span v-if="savingAddress">
              <div class="spinner-border ml-2" />
            </span>
          </button>
          <!-- Validation footer buttons -->
          <div
            v-if="!useExistingBillingInformation && isShowingValidationView && !showContactSupportButton"
            class="row w-100 m-0"
          >
            <div class="col pr-1">
              <button
                variant="outline-warning"
                class="btn btn-outline-warning btn-block"
                @click="editAddress"
                data-test-hook="edit-address-button"
              >
                {{ $t('xAddressSearchEditAddress') }}
              </button>
            </div>
            <div class="col pl-1">
              <button
                variant="warning"
                class="btn btn-warning btn-block"
                @click="confirmAddress"
                :disabled="confirmAddressDisabled"
                data-test-hook="confirm-address-button"
              >
                {{ $t('xAddressSearchConfirmAddress') }}
                <span v-if="savingAccountHolder">
                  <div class="spinner-border ml-2" />
                </span>
                <span v-else>
                  <i class="far fa-arrow-right"></i>
                </span>
              </button>
            </div>
          </div>
          <!-- Validation contact support footer buttons -->
          <div
            v-if="!useExistingBillingInformation && isShowingValidationView && showContactSupportButton"
            class="row w-100 m-0"
          >
            <div class="col pr-1">
              <button
                variant="outline-warning"
                class="btn btn-outline-warning btn-block"
                @click="editAddress"
                data-test-hook="edit-address-button"
              >
                {{ $t('xAddressSearchEditAddress') }}
              </button>
            </div>
            <div class="col pl-1">
              <button
                v-if="!useExistingBillingInformation && isShowingValidationView && showContactSupportButton"
                variant="warning"
                class="btn btn-warning btn-block"
                @click="$router.push({ path: `/support` })"
                data-test-hook="contact-support-button"
              >
                {{ $t('xContactSupport') }}
              </button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import * as Constants from '@/constants'
import { required, minLength, maxLength, numeric } from 'vuelidate/lib/validators'
import AddressSearchModals from '@/components/Common/Address/AddressSearchModals'
import { isAddressValid } from '../../utils/address'
import { subscriptionDetailActive, subscriptionActive, billingMaintenanceFeatureFlagEnabled } from '../../utils/mixins'

const monthInRange = (value, vm) => value >= 1 && value <= 12

export default {
  components: {
    AddressSearchModals
  },
  mixins: [validationMixin],
  data() {
    return {
      isLoading: true,
      accountData: null,
      creditCardNumber: null,
      cvv: null,
      expirationMonth: null,
      expirationYear: null,
      paying: false,
      paymentInfo: null,
      removing: false,
      shouldShowRemovePayment: false,
      shouldShowUpdatePayment: false,
      showValidationBanner: false,
      useExistingBillingInformation: true,
      subscriptionDetails: null,
      activeSubscriptionPlatform: null,
      subscriptionIsDunning: false,
      addressFormDisabled: false,
      isShowingValidationView: false,
      savingAddress: false,
      savingAccountHolder: false,
      addressSearchError: false
    }
  },
  validations: {
    cvv: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(4),
      numeric
    },
    expirationMonth: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric,
      monthInRange
    },
    expirationYear: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric
    },
    creditCardNumber: {
      required,
      min: minLength(15),
      max: maxLength(16),
      minLength: minLength(15),
      maxLength: maxLength(16),
      numeric
    },
    consent: {
      required,
      Boolean
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    appleStore() {
      return Constants.AppleSubscriptionStore
    },
    googleStore() {
      return Constants.GoogleSubscriptionStore
    },
    isMobileSubscription() {
      return (
        this.activeSubscriptionPlatform === Constants.AppleSubscriptionStore ||
        this.activeSubscriptionPlatform === Constants.GoogleSubscriptionStore
      )
    },
    confirmAddressDisabled() {
      return !this.$refs.updateCardAddressSearchModalsRef?.selectedAddress
    },
    showContactSupportButton() {
      return this.$refs.updateCardAddressSearchModalsRef?.showContactSupportButton
    }
  },
  methods: {
    canPay() {
      if (!this.creditCardNumber || !this.cvv || !this.expirationMonth || !this.expirationYear) {
        return false
      }
      if (!this.$v.$anyError && !this.paying) {
        return true
      } else return false
    },
    displayMonth(month) {
      let monthString = String(month)
      if (monthString.length === 1) return '0' + month
      else return month
    },
    async removePaymentInformation() {
      this.removing = true
      let removePaymentModel = {
        organizationId: this.accountProfile.organizationId,
        paymentId: this.paymentInfo.id
      }
      this.$store
        .dispatch('removeBillingAccountPaymentMethod', removePaymentModel)
        .then(response => {
          this.paymentInfo = null
          this.$store.dispatch('displayToast', {
            title: this.$t('xSuccess'),
            message: this.$t('xPaymentMethodRemoved'),
            variant: 'success'
          })
          this.shouldShowRemovePayment = false
          this.removing = false
        })
        .catch(error => {
          this.$store.dispatch('displayToast', {
            title: this.$t('xRemoveFailed'),
            message: this.$t('xUnableToRemovePaymentInfo'),
            variant: 'danger'
          })
          this.removing = false
        })
    },
    closePaymentModal() {
      this.creditCardNumber = null
      this.expirationMonth = null
      this.expirationYear = null
      this.cvv = null
      this.paying = false
      this.showValidationBanner = false
      this.errorMessages = []
      this.$v.$reset()
      this.closeModal('update-payment-method')
    },
    payForProduct() {
      this.paying = true
      let addPaymentModel = {
        organizationId: this.accountProfile.organizationId,
        cardNumber: this.creditCardNumber,
        cvv: this.cvv,
        expirationMonth: this.expirationMonth,
        expirationYear: '20' + this.expirationYear
      }
      this.$store
        .dispatch('addBillingAccountPaymentMethod', addPaymentModel)
        .then(response => {
          this.$store
            .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
            .then(paymentResponse => {
              if (paymentResponse.data) {
                this.paymentInfo = paymentResponse.data
                this.useExistingPaymentInfo = true
              } else {
                this.paymentInfo = null
                this.useExistingPaymentInfo = false
              }
            })
            .catch(error => {
              this.paymentInfo = null
              this.useExistingPaymentInfo = false
            })
            .finally(() => {
              this.closePaymentModal()
              this.$store.dispatch('displayToast', {
                title: this.$t('xSuccess'),
                message: this.$t('xPaymentMethodUpdated'),
                variant: 'success'
              })
            })
        })
        .catch(error => {
          this.errorMessages = []
          if (error.response.data.errors) {
            if (error.response.data.errors.CardNumber || error.response.data.errors.cardNumber) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCCIsInvalid'))
            }
            if (error.response.data.errors.Cvv || error.response.data.errors.cvv) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCvvInvalid'))
            }
            if (error.response.data.errors.ExpirationMonth || error.response.data.errors.expirationMonth) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationMonthInvalid'))
            }
            if (error.response.data.errors.ExpirationYear || error.response.data.errors.expirationYear) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationYearInvalid'))
            }
            if (error.response.data.errors.billing || error.response.data.errors.Billing) {
              this.$store.dispatch('displayToast', {
                title: this.$t('xSomethingWentWrong'),
                message: error.response.data.errors.billing
                  ? error.response.data.errors.billing
                  : error.data.response.errors.Billing,
                variant: 'danger'
              })
            }
          } else {
            this.$store.dispatch('displayToast', {
              title: this.$t('xSomethingWentWrong'),
              message: this.$t('xFailedToUpdatePaymentMethod'),
              variant: 'danger'
            })
          }
        })
        .finally(() => {
          this.paying = false
        })
    },
    async saveAccountHolder(newAddress, responseOptions) {
      this.savingAccountHolder = true
      if (newAddress) {
        const requestBody = {
          firstName: this.accountData.firstName,
          lastName: this.accountData.lastName,
          organizationAddress: {
            type: newAddress.addressType,
            line1: newAddress.address1,
            line2: newAddress.address2,
            city: newAddress.city,
            region: newAddress.state,
            country: newAddress.countryCode,
            postalCode: newAddress.postalCode,
            organizationId: this.accountData.organizationId
          }
        }
        await this.$store
          .dispatch('updateAccountHolder', requestBody)
          .then(async result => {
            if (result) {
              await this.$store.dispatch('getAccountProfile')
              await this.$store.dispatch('getAccountInfo')
              responseOptions.closeAddressModals()
              this.useExistingBillingInformation = true
            } else {
              responseOptions.openAddressFormWithError()
            }
          })
          .catch(error => {
            responseOptions.openAddressFormWithError()
          })
      }
      this.isShowingValidationView = false
      this.savingAccountHolder = false
    },
    async addBillingAddress() {
      this.savingAddress = true
      await this.$refs.updateCardAddressSearchModalsRef.verifyAddress()
      this.savingAddress = false
      if (!this.$refs.updateCardAddressSearchModalsRef.isFormInvalid) this.handleValidationStateChange(true)
    },
    handleValidationStateChange(isShowing) {
      this.isShowingValidationView = isShowing
    },
    editAddress() {
      this.handleValidationStateChange(false)
      this.$refs.updateCardAddressSearchModalsRef.openAddressFormModal()
      this.$refs.updateCardAddressSearchModalsRef.goBackToAddressForm()
    },
    confirmAddress() {
      this.$refs.updateCardAddressSearchModalsRef.saveAndSubmit()
    },
    handleSetIsNextDisabled(isDisabled) {
      this.addressFormDisabled = isDisabled
    },
    handleAddressSearchError(hasError) {
      this.addressSearchError = hasError
    },
    async getPaymentMethod() {
      await this.$store
        .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
        .then(response => {
          this.paymentInfo = response.data ? response.data : null
        })
        .catch(error => {
          this.paymentInfo = null
        })
    }
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
    } else {
      this.$store.dispatch('getAccountProfile')
    }

    if (!this.currentAppConfig) {
      this.$store.dispatch('getApplicationConfiguration', 'Any')
    }

    await this.getPaymentMethod()
    await this.$store
      .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
      .then(response => {
        this.subscriptionDetails = response.data
        const activeIapSubscriptionDetails = this.subscriptionDetails.subscriptions?.find(
          subscription =>
            subscription.store !== Constants.DealerSubscriptionStore && subscriptionDetailActive(subscription.status)
        )
        const activeLegacySubscriptionDetails = this.subscriptionDetails?.legacyBillingProducts?.products?.find(
          product => subscriptionActive(product)
        )
        const dunningLegacySubscriptionDetails = this.subscriptionDetails?.legacyBillingProducts?.products?.find(
          product => product?.dunningState === Constants.DunningInProgress
        )

        //check for iap sub
        if (activeIapSubscriptionDetails) this.activeSubscriptionPlatform = activeIapSubscriptionDetails.store
        //check for legacy sub
        else if (activeLegacySubscriptionDetails) this.activeSubscriptionPlatform = Constants.WebSubscriptionStore

        //check for grace period iap sub
        if (activeIapSubscriptionDetails?.reason === Constants.SubscriptionDetailsReasonGracePeriodStarted)
          this.subscriptionIsDunning = true
        //check for dunning legacy sub
        else if (dunningLegacySubscriptionDetails) this.subscriptionIsDunning = true

        if (this.paymentInfo) {
          if (activeIapSubscriptionDetails || activeLegacySubscriptionDetails) {
            if (this.isMobileSubscription) this.shouldShowRemovePayment = true
            else this.shouldShowUpdatePayment = true
          } else this.shouldShowRemovePayment = true
        }
      })
      .catch(() => {})
    this.isLoading = false
  }
}
</script>

<style scoped>
.btn-update {
  background-color: var(--carbon-900) !important;
}

.btn-update:hover {
  background-color: var(--carbon-800) !important;
}

.btn-update:pressed {
  background-color: var(--black) !important;
}
</style>
